import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useMember } from "api/members";
import { TextInput } from "components/FormControls/TextInput";
import { ImportXmlModal } from "components/Layout/Import/ImportXmlModal";
import { AddUnitsModal } from "components/Layout/Unit/UnitTransfersList/AddUnitsModal";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ImportTypes } from "types/import";
import { PointsTransferTypes } from "types/unit-transfers";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { Modals } from "./MemberList";
import { useStyles } from "./MemberList.styles";
import { AssignToTier } from "./Modals/AssignToTier";
import { MemberDetailsDrawer } from "./Modals/MemberDetailsDrawer";
import { RemoveAssignedTier } from "./Modals/RemoveAssignedTier";

interface MemberModalsProps {
  close: () => void;
  open: boolean;
  selectedModal?: { member: string; mode: number };
  setModal?: (modal: { member: string; mode: number }) => void;
}

export function MemberModals({
  close,
  open,
  selectedModal,
  setModal,
}: MemberModalsProps) {
  const classes = useStyles();
  const { message } = useMessage();
  const {
    activateMember,
    anonymizeMember,
    changeMemberPassword,
    deactivateMember,
    removeManuallyAssignedTier,
    removeMember,
  } = useMember();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const messages = useMemo(
    () => ({
      activateMember: {
        successMessage: "memberActivated",
        endpoint: activateMember,
      },
      deactivateMember: {
        successMessage: "memberDeactivated",
        endpoint: deactivateMember,
      },
      anonymizeMember: {
        successMessage: "memberAnonymized",
        endpoint: anonymizeMember,
      },
      removeMember: {
        successMessage: "memberRemoved",
        endpoint: removeMember,
      },
      removeManuallyAssignedTier: {
        successMessage: "tierRemoved",
        endpoint: removeManuallyAssignedTier,
      },
    }),
    [
      activateMember,
      deactivateMember,
      anonymizeMember,
      removeMember,
      removeManuallyAssignedTier,
    ],
  );

  const submitModal = useCallback(
    async (
      mode:
        | "activateMember"
        | "deactivateMember"
        | "anonymizeMember"
        | "removeMember"
        | "removeManuallyAssignedTier",
      customer: string,
    ) => {
      const successMessage = () => messages[mode]?.successMessage;
      const endpoint = async () => messages[mode]?.endpoint?.(customer);
      try {
        const response = await endpoint();
        if (response) {
          close();
          notify(message({ id: successMessage() }));
          mode === "removeMember" && navigate(links.membersList);
        }
      } catch (error) {
        notify(getErrorMessage(error));
      }
    },
    [close, navigate, message, messages, notify],
  );

  return (
    <>
      {selectedModal?.mode === Modals.Activate ? (
        <ConfirmationModal
          action={() =>
            submitModal("activateMember", selectedModal?.member as string)
          }
          close={close}
          header="activateAccountHeader"
          info="activateAccountInfo"
          open={open}
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Deactivate ? (
        <ConfirmationModal
          action={() =>
            submitModal("deactivateMember", selectedModal?.member as string)
          }
          close={close}
          header="thisAccountWillBeDeactivated"
          info="deactivateThisAccount"
          open={open}
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Anonymize ? (
        <ConfirmationModal
          action={() =>
            submitModal("anonymizeMember", selectedModal?.member as string)
          }
          close={close}
          header="memberAnonymizationHeader"
          info="memberAnonymizationInfo"
          open={open}
          data-test-id="anonymize-member"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.Remove ? (
        <ConfirmationModal
          action={() =>
            submitModal("removeMember", selectedModal?.member as string)
          }
          close={close}
          header="deleteHeader"
          info="deleteMemberInfo"
          open={open}
          data-test-id="remove-member"
          modalWidth={454}
        />
      ) : selectedModal?.mode === Modals.RemoveManuallyAssignedTier ? (
        <RemoveAssignedTier
          isOpen={open}
          closeModal={close}
          memberId={selectedModal.member}
        />
      ) : selectedModal?.mode === Modals.Add ? (
        <AddUnitsModal
          closeModal={() => close()}
          initialMember={selectedModal?.member}
          initialMode={PointsTransferTypes.ADD_POINTS}
          isModalOpen={open}
          setModal={setModal}
        />
      ) : selectedModal?.mode === Modals.Deduct ? (
        <AddUnitsModal
          closeModal={() => close()}
          initialMember={selectedModal?.member}
          initialMode={PointsTransferTypes.SPEND_POINTS}
          isModalOpen={open}
          setModal={setModal}
        />
      ) : selectedModal?.mode === Modals.ImportMembers ? (
        <ImportXmlModal
          isModalOpen={open}
          closeModal={() => close()}
          type={ImportTypes.MEMBER}
          navigate={() =>
            navigate(links.importsExports, { state: { activeTab: "imports" } })
          }
          data-test-id="members-list-import-members-modal"
        />
      ) : selectedModal?.mode === Modals.AssignToTier ? (
        <AssignToTier
          isOpen={open}
          closeModal={close}
          memberId={selectedModal.member}
        />
      ) : selectedModal?.mode === Modals.MemberDetailsDrawer ? (
        <MemberDetailsDrawer
          memberId={selectedModal.member}
          onClose={() => close()}
          open={open}
        />
      ) : selectedModal?.mode === Modals.ChangePassword ? (
        <OLModal
          header={message({ id: "changePassword" })}
          onClose={() => close()}
          open={open}
        >
          <Formik
            initialValues={{ plainPassword: undefined }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});
              try {
                const response = await changeMemberPassword(
                  selectedModal?.member,
                  values.plainPassword!,
                );
                if (response) {
                  close();
                  notify(message({ id: "passwordUpdated" }));
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({ handleSubmit, isSubmitting }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <TextInput
                    className={classes.SelectInput}
                    data-test-id="new-password-input"
                    label={message({ id: "newPassword" })}
                    name="plainPassword"
                    type="password"
                    data-hj-suppress
                  />
                  <OLButton
                    data-test-id="change-password-button"
                    className={classes.modalButton}
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    {message({ id: "change" })}
                  </OLButton>
                </Form>
              );
            }}
          </Formik>
        </OLModal>
      ) : null}
    </>
  );
}
